<template>
  <div v-if="show" :class="`weeks-grid weeks-grid--${size}`" @click.stop>
    <div
      v-for="week in weeksInYear"
      :key="formatMoment(week.weekStart, 'gggg-WW')"
      :class="clsx('week-item', {
        'current-week': week.isCurrentWeek,
        'today-week': week.isTodayWeek,
        'selected': week.isSelected,
        'in-range': week.inRange,
        'range-start': week.isRangeStart,
        'range-end': week.isRangeEnd,
        'is-disabled': week.isDisabled
      })"
      @click="selectWeek(week.weekStart)"
      :aria-label="`Select week ${week.weekNumber}`"
    >
      <span class="week-number">{{ week.weekNumber }}</span>
      <span class="week-dates">{{ formatMoment(week.weekStart, 'DD.MM') }} - {{ formatMoment(week.weekEnd, 'DD.MM') }}</span>
    </div>
  </div>
</template>

<script>
import clsx from 'clsx';
import { moment } from '../../lib';

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'normal',
      validator: (value) => ['normal', 'small'].includes(value),
    },
    currentDate: {
      type: Object,
      required: true,
    },
    selectedYear: {
      type: Number,
      required: true,
    },
    range: {
      type: Boolean,
      default: false,
    },
    startDate: {
      type: Object,
      default: null,
    },
    endDate: {
      type: Object,
      default: null,
    },
    maxRangeLimit: {
      type: Number,
      default: null,
    },
    limitUnit: {
      type: String,
      default: 'week',
      validator: (value) => ['day', 'week', 'month'].includes(value),
    },
    selectingRange: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [ moment ],
  computed: {
    weeksInYear() {
      const weeks = [];
      const today = this.getMoment();
      const currentYear = this.getYearMoment(today);

      let current = this.setYearMoment(this.getMoment(), this.selectedYear);
      current = this.startOfMoment(current, 'year');
      current = this.startOfMoment(current, 'week');

      let iterationLimit = this.setYearMoment(this.getMoment(), this.selectedYear + 1);
      iterationLimit = this.startOfMoment(iterationLimit, 'year');
      iterationLimit = this.startOfMoment(iterationLimit, 'week');

      const selectedYearStart = this.startOfMoment(this.setYearMoment(this.getMoment(), this.selectedYear), 'year');
      const selectedYearEnd = this.endOfMoment(this.setYearMoment(this.getMoment(), this.selectedYear), 'year');

      while (this.isBeforeMoment(current, iterationLimit, 'week')) {
        const weekStart = this.cloneMoment(current);
        const weekEnd = this.endOfMoment(this.cloneMoment(current), 'week');
        const isoWeekYearOfCurrentWeek = this.getISOWeekYearMoment(weekStart);

        if (isoWeekYearOfCurrentWeek === this.selectedYear) {
          const weekObj = {
            weekNumber: this.getWeekMoment(weekStart),
            weekStart: weekStart,
            weekEnd: weekEnd,
            isTodayWeek: this.isSameMoment(weekStart, today, 'week') && isoWeekYearOfCurrentWeek === currentYear,
            isCurrentWeek: this.isSameMoment(weekStart, this.currentDate, 'week') && isoWeekYearOfCurrentWeek === currentYear,
            isSelected: false,
            inRange: false,
            isRangeStart: false,
            isRangeEnd: false,
            isDisabled: false,
          };

          if (this.range) {
            weekObj.isSelected = (this.startDate && this.isSameMoment(weekStart, this.startDate, 'week')) ||
              (this.endDate && this.isSameMoment(weekStart, this.endDate, 'week'));
            weekObj.isRangeStart = this.startDate && this.isSameMoment(weekStart, this.startDate, 'week');
            weekObj.isRangeEnd = this.endDate && this.isSameMoment(weekStart, this.endDate, 'week');

            if (this.startDate && this.endDate) {
              const effectiveStartDate = this.isBeforeMoment(this.startDate, selectedYearStart, 'week') ? selectedYearStart : this.startDate;
              const effectiveEndDate = this.isAfterMoment(this.endDate, selectedYearEnd, 'week') ? selectedYearEnd : this.endDate;

              weekObj.inRange = this.isSameOrAfterMoment(weekStart, effectiveStartDate, 'week') &&
                this.isSameOrBeforeMoment(weekStart, effectiveEndDate, 'week') &&
                this.getYearMoment(weekStart) === this.selectedYear;
            }
          } else {
            weekObj.isSelected = this.startDate && this.isSameMoment(weekStart, this.startDate, 'week');
          }

          weekObj.isDisabled = this.isDisabledWeek(weekStart);
          weeks.push(weekObj);
        }

        current = this.addMoment(current, 1, 'week');
      }

      return weeks;
    },
  },
  methods: {
    clsx,
    selectWeek(weekStartMoment) {
      if (!this.isDisabledWeek(weekStartMoment)) {
        this.$emit('select-week', weekStartMoment);
      }
    },
    isDisabledWeek(weekStartMoment) {
      if (!this.range || this.maxRangeLimit === null || !this.startDate) {
        return false;
      }

      if (this.startDate && this.endDate) {
        return false;
      }

      if (this.selectingRange) {
        const weekEndMoment = this.endOfMoment(this.cloneMoment(weekStartMoment), 'week');
        let rangeStart = this.startDate;
        let rangeEnd = weekEndMoment;

        if (this.isAfterMoment(rangeStart, rangeEnd, this.limitUnit)) {
          [rangeStart, rangeEnd] = [rangeEnd, rangeStart];
        }

        let calculatedDifference;

        if (this.limitUnit === 'week') {
          const startWeek = this.getWeekMoment(rangeStart);
          const endWeek = this.getWeekMoment(rangeEnd);
          const startYear = this.getISOWeekYearMoment(rangeStart);
          const endYear = this.getISOWeekYearMoment(rangeEnd);

          if (startYear === endYear) {
            calculatedDifference = Math.abs(endWeek - startWeek) + 1;
          } else {
            const weeksInStartYear = this.getISOWeeksInYear(startYear);
            const weeksInEndYear = endWeek;

            let fullYearsWeeks = 0;
            for (let year = startYear + 1; year < endYear; year++) {
              fullYearsWeeks += this.getISOWeeksInYear(year);
            }

            calculatedDifference = (weeksInStartYear - startWeek + 1) + fullYearsWeeks + weeksInEndYear;
          }
        } else if (this.limitUnit === 'month') {
          const effectiveRangeStart = this.startOfMoment(this.cloneMoment(rangeStart), 'month');
          const effectiveRangeEnd = this.endOfMoment(this.cloneMoment(rangeEnd), 'month');
          calculatedDifference = this.getMonthsDifference(effectiveRangeStart, effectiveRangeEnd);
        } else {
          const effectiveRangeStart = this.cloneMoment(rangeStart);
          calculatedDifference = this.getDaysDifference(effectiveRangeStart, weekEndMoment);
        }

        return calculatedDifference > this.maxRangeLimit;
      }

      return false;
    },
    getMonthsDifference(startMoment, endMoment) {
      const d1 = this.toDateMoment(startMoment);
      const d2 = this.toDateMoment(endMoment);
      const yearsDiff = d2.getFullYear() - d1.getFullYear();
      const monthsDiff = d2.getMonth() - d1.getMonth();
      return (yearsDiff * 12) + monthsDiff + 1;
    },
    getDaysDifference(startMoment, endMoment) {
      const d1 = this.toDateMoment(startMoment);
      const d2 = this.toDateMoment(endMoment);
      const diffTime = Math.abs(d2.getTime() - d1.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays + 1;
    },
  },
};
</script>

<style lang="scss" scoped>
$small-scale-factor: 0.8;
$weeks-grid-item-font-size: 16px;
$weeks-grid-item-padding: 8px;

.weeks-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  background-color: #fff;
  padding: 10px;

  &--small {
    .week-item {
      font-size: $weeks-grid-item-font-size * $small-scale-factor;
      padding: $weeks-grid-item-padding * $small-scale-factor;
    }
  }
}

.week-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  font-size: $weeks-grid-item-font-size;
  font-weight: 400;
  text-align: center;
  padding: $weeks-grid-item-padding;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.1s ease;

  &:hover {
    background-color: #ededed;
  }

  &:active {
    background-color: #e0e0e0;
    transform: scale(0.98);
  }

  .week-number {
    font-weight: 600;
    margin-bottom: 4px;
  }

  .week-dates {
    font-size: 0.8em;
    color: #09090B;
  }

  &.current-week {
    background-color: #f0f0f0;
    font-weight: 600;
  }

  &.today-week {
    background-color: #fb923c;
    color: white;
    font-weight: 600;

    .week-dates {
      color: rgba(255, 255, 255, 0.8);
    }
  }

  &.selected {
    background-color: #EDEDED;
    color: #09090B;
    font-weight: 500;

    &:hover {
      background-color: darken(#EDEDED, 5%);
    }

    .week-dates {
      color: rgba(9, 9, 11, 0.8);
    }
  }

  &.in-range {
    background-color: #EDEDED;
    font-weight: 500;
    color: inherit;

    &:hover {
      background-color: darken(#EDEDED, 5%);
    }
  }

  &.range-start,
  &.range-end {
    background-color: #EDEDED;
    font-weight: 500;
  }

  &.is-disabled {
    cursor: not-allowed;
    pointer-events: none;
    opacity: 0.3;
    background-color: #FCE4E4;
    color: #999;

    &:hover {
      background-color: #FCE4E4;
    }

    &:active {
      transform: none;
    }

    .week-dates {
      color: #999;
    }
  }
}
</style>
